import React, { useState, useEffect, useRef } from 'react';
import MainStructure from '../../components/general/MainStructure';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { resizeImage } from '../../components/general/resizeImage';

function Settings() {
  const navigate = useNavigate();
  const { name, setname, picture, setpicture } = useAuth();

  // ── Profile section ──────────────────────────────────────────────────────
  const [profileName, setProfileName] = useState('');
  const [previewPic, setPreviewPic] = useState(null);
  const [picBase64, setPicBase64] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const fileRef = useRef();

  useEffect(() => {
    if (name && name !== 'User') setProfileName(name);
  }, [name]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { setProfileError('Image must be under 10MB.'); return; }
    try {
      const resized = await resizeImage(file);
      setPreviewPic(resized);
      setPicBase64(resized);
    } catch {
      setProfileError('Could not process image. Please try another file.');
    }
  };

  const handleProfileSave = async () => {
    if (!profileName.trim()) { setProfileError('Name cannot be empty.'); return; }
    setProfileLoading(true);
    setProfileError('');
    setProfileSuccess('');
    try {
      const res = await fetch('https://api.schedpilot.com/wp-json/api/v1/update-profile', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: profileName.trim(), picture_base64: picBase64 || null }),
      });
      const data = await res.json();
      if (res.ok && data.code === 200) {
        setname(profileName.trim());
        if (data.picture_url) setpicture(data.picture_url);
        setPicBase64(null);
        setProfileSuccess('Profile updated successfully!');
      } else {
        setProfileError(data.message || 'Failed to update profile.');
      }
    } catch {
      setProfileError('Connection error. Please try again.');
    } finally {
      setProfileLoading(false);
    }
  };

  // ── Preferences + password section ───────────────────────────────────────
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [timezone, setTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [dailySummaryEmail, setDailySummaryEmail] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const timezones = Intl.supportedValuesOf('timeZone').map(tz => ({
    label: tz.replaceAll('_', ' '),
    value: tz
  }));

  // --- NEW: Fetch settings on load ---
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch('https://api.schedpilot.com/wp-json/schedpilot/v1/get-settings', {
          method: 'GET',
          credentials: 'include',
        });
        const data = await response.json();
        if (response.ok && data.timezone) {
          setTimezone(data.timezone);
        }
        if (response.ok && data.daily_summary_email !== undefined) {
          setDailySummaryEmail(data.daily_summary_email);
        }
      } catch (err) {
        console.error("Failed to fetch user settings", err);
      }
    };

    fetchSettings();
  }, []);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const isChangingPassword = currentPassword || newPassword || confirmPassword;

    if (isChangingPassword) {
      if (!currentPassword || !newPassword || !confirmPassword) {
        setError('All password fields are required to change your password.');
        return;
      }
      if (newPassword !== confirmPassword) {
        setError('New password and confirm password do not match.');
        return;
      }
      if (newPassword.length < 8) {
        setError('New password must be at least 8 characters long.');
        return;
      }
    }

    setLoading(true);

    try {
      const response = await fetch('https://api.schedpilot.com/wp-json/schedpilot/v1/update-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include', 
        body: JSON.stringify({
          timezone: timezone,
          daily_summary_email: dailySummaryEmail,
          current_password: currentPassword,
          new_password: newPassword,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setSuccess('Settings updated successfully!');
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        setError(result.message || 'Failed to update settings.');
      }
    } catch (err) {
      setError('A connection error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <MainStructure activeItem="settings">
      <div className="container py-5">
        <div className="row">
          <div className="col-md-8 col-lg-6 mx-auto">
            <div className="card shadow-sm">
              <div className="card-body">
                <h1 className="h3 mb-3 font-weight-bold">Settings</h1>
                <p className="text-muted mb-4">Manage your account settings and preferences</p>

                {/* ── Profile ── */}
                <h5 className="mb-3 font-weight-bold">Profile</h5>
                <div className="d-flex align-items-center gap-3 mb-3">
                  <div
                    onClick={() => fileRef.current.click()}
                    title="Click to change photo"
                    style={{
                      width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
                      overflow: 'hidden', cursor: 'pointer',
                      border: '2px dashed #ced4da',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      background: '#f8f9fa',
                    }}
                  >
                    <img
                      src={previewPic || picture}
                      alt="avatar"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="form-label mb-1">Display Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={profileName}
                      onChange={e => setProfileName(e.target.value)}
                      placeholder="Your full name"
                    />
                    <div style={{ fontSize: '0.78rem', color: '#6c757d', marginTop: 4 }}>
                      Click the avatar to change your photo (max 2MB)
                    </div>
                  </div>
                </div>
                {profileError && <div className="alert alert-danger py-2">{profileError}</div>}
                {profileSuccess && <div className="alert alert-success py-2">{profileSuccess}</div>}
                <button
                  type="button"
                  className="btn btn-outline-primary mb-4"
                  onClick={handleProfileSave}
                  disabled={profileLoading}
                >
                  {profileLoading ? 'Saving…' : 'Save Profile'}
                </button>

                <hr className="mb-4" />

                <form onSubmit={handleUpdate}>
                  <h5 className="mb-3 font-weight-bold">Preferences</h5>
                  <div className="form-group mb-4">
                    <label htmlFor="timezone" className="form-label">Timezone</label>
                    <select 
                      id="timezone"
                      className="form-control"
                      value={timezone}
                      onChange={(e) => setTimezone(e.target.value)}
                    >
                      {timezones.map((tz) => (
                        <option key={tz.value} value={tz.value}>
                          {tz.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group mb-4">
                    <label className="form-label d-block">Notifications</label>
                    <div
                      className="d-flex align-items-center justify-content-between p-3"
                      style={{ border: '1px solid #dee2e6', borderRadius: 10, background: '#f8f9fa' }}
                    >
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#212529' }}>Daily Summary Email</div>
                        <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: 2 }}>
                          Receive a morning recap of yesterday's posts and 7-day analytics
                        </div>
                      </div>
                      <div
                        onClick={() => setDailySummaryEmail(v => !v)}
                        style={{
                          width: 44, height: 24, borderRadius: 12, flexShrink: 0, cursor: 'pointer',
                          background: dailySummaryEmail ? '#2fba01' : '#ced4da',
                          position: 'relative', transition: 'background 0.2s',
                        }}
                      >
                        <div style={{
                          position: 'absolute', top: 3, left: dailySummaryEmail ? 23 : 3,
                          width: 18, height: 18, borderRadius: '50%', background: '#fff',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s',
                        }} />
                      </div>
                    </div>
                  </div>

                  <hr className="my-4" />

                  <h5 className="mb-3 font-weight-bold">Change Password</h5>
                  <div className="form-group mb-3">
                    <label className="form-label">Current Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      placeholder="Enter current password"
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="form-label">New Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      placeholder="Enter new password"
                    />
                  </div>
                  <div className="form-group mb-3">
                    <label className="form-label">Confirm New Password</label>
                    <input
                      type="password"
                      className="form-control"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm new password"
                    />
                  </div>

                  {error && <div className="alert alert-danger">{error}</div>}
                  {success && <div className="alert alert-success">{success}</div>}
                  
                  <button type="submit" className="btn btn-primary w-100 mb-3" disabled={loading}>
                    {loading ? 'Saving Changes...' : 'Update Settings'}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MainStructure>
  );
}

export default Settings;